import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import OperatorForm from './OperatorForm';
import { useNavigate, useOutletContext } from 'react-router';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import { OperatorTemplateContext } from './types';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const CreateOperatorPage = () => {
  const { setDisplayedOperatorName } =
    useOutletContext<OperatorTemplateContext>();
  const { createOperator } = useIotsApi();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const mutation = useMutation({ mutationFn: createOperator });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  return (
    <OperatorForm
      isEditingByDefault
      onNameChange={setDisplayedOperatorName}
      onSubmit={values => {
        mutation.mutate(
          {
            name: values.companyName,
            webSite: values.website || undefined,
            trnVatNumber: values.trnVatNumber,
            tcanNumber: values.tcanNumber,
            croNumber: values.croNumber,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            addressLine3: values.addressLine3,
            county: values.county,
            eircode: values.eirCode,
            phone: values.companyPhoneNumber,
            email: values.companyEmailAddress,
          },
          {
            onSuccess: operatorId => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.operator-creation-success'),
              });
              void disableNavigationGuard(() =>
                navigate(`/admin/operators/${operatorId}`),
              );
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.operator-creation-error'),
                ),
              });
              mutation.reset();
            },
          },
        );
      }}
      onCancel={() => void navigate('/admin/operators')}
      isLoading={mutation.isPending}
    />
  );
};

export default CreateOperatorPage;
